import PhoneNumber from '@/library/form/validator/PhoneNumber';
import axios from 'axios';
import {type ComponentPublicInstance} from 'vue';
import FormSteps from '../../components/form/FormSteps.vue';
import FlashMessenger from '../../library/flashMessenger';
import TopMessageHandler from '../../library/topMessageHandler';
import store from '../../store';
import {errorHandler, XHR_STATUS_DEFAULT, XHR_STATUS_INVALID} from '../../utils/portuna';
import {getVueParent, scrollToContentElement, scrollToTop} from '../dom';
import {NoOperation} from '../utils';
import Loader from './../../library/intranet/loader';
import AddProtocol from './filter/AddProtocol';
import StringTrim from './filter/StringTrim';
import StripTags from './filter/StripTags';
import ToArray from './filter/ToArray';
import ToInt from './filter/ToInt';
import ToNull from './filter/ToNull';
import ToString from './filter/ToString';
import Between from './validator/Between';
import BuyerReference from './validator/BuyerReference';
import DateTime from './validator/DateTime';
import DateValidator from './validator/DateValidator';
import EmailAddress from './validator/EmailAddress';
import ExplodeValidator from './validator/ExplodeValidator';
import GreaterThan from './validator/GreaterThan';
import Hostname from './validator/Hostname';
import Iban from './validator/Iban';
import Identical from './validator/Identical';
import InArray from './validator/InArray';
import LessThan from './validator/LessThan';
import NotEmailAddress from './validator/NotEmailAddress';
import NotEmpty from './validator/NotEmpty';
import NotInArray from './validator/NotInArray';
import NumberValidator from './validator/NumberValidator';
import Regex from './validator/Regex';
import StringLength from './validator/StringLength';
import Time from './validator/Time';
import Vatin from './validator/Vatin';

interface ValidatorResponse
{
    validate: boolean;
    error: string;
}

interface ValidatorOptions
{
    [index: string]: any;
}

interface FilterOptions extends ValidatorOptions
{
}

type FormSubmitOptions = {
    resolveFullResponse: boolean;
    ignoreScroll: boolean;
    messageType: 'flashMessenger' | 'topMessage' | 'none';
    formSteps?: Nullable<typeof FormSteps>;
    noLoaderHide?: boolean;
    noLoader?: boolean;
    noMessage?: boolean;
}

function formSubmit(url: string, formOptions: Partial<FormSubmitOptions> | Record<string, any>, data?: object): Promise<any>
{
    let options: DynamicObjectExtend<FormSubmitOptions, Record<string, any>> = {
        resolveFullResponse: false,
        ignoreScroll: false,
        messageType: 'topMessage',
        noLoader: false,
    };

    if (data !== undefined)
    {
        options = Object.assign(options, formOptions);
    }

    if (true !== options.noLoader)
    {
        Loader.show('Formular wird gesendet...');
    }

    return new Promise((resolve, reject) =>
    {
        // reset errors before submit
        store.dispatch('form/setErrors', {}).catch(console.error);

        // set the form state to submitted
        store.commit('form/setSubmitted');

        if (data === undefined)
        {
            data = formOptions;
        }

        axios.post(url, data)
        .then((response) =>
        {
            // saved
            if (response.data.status === XHR_STATUS_DEFAULT)
            {
                resolve(options.resolveFullResponse ? response.data : response.data.data);
            }
            // set form errors
            else
            {
                let promise: Nullable<Promise<void>> = null;
                if (response.data.status === XHR_STATUS_INVALID)
                {
                    store.dispatch('form/setErrors', response.data.data?.errors || []).then();

                    if (options.formSteps)
                    {
                        // @ts-ignore
                        promise = options.formSteps.openInvalidTab();
                    }
                }

                if (!promise)
                {
                    promise = Promise.resolve();
                }

                promise.finally(() =>
                {
                    if (!options.ignoreScroll)
                    {
                        const firstError: Nullable<HTMLDivElement> = document.querySelector('.form-error');

                        // scroll to first error
                        if (firstError)
                        {
                            const firstFormError  = firstError.__vue__ as ComponentPublicInstance | undefined;
                            const scrollToElement = firstFormError && getVueParent(firstFormError, 'FormRow')?.$el || firstError;
                            let isInsidePopUp     = false;

                            for (const popupBody of [...document.querySelectorAll('.popup .popup-body')])
                            {
                                if (!popupBody.contains(firstError))
                                {
                                    continue;
                                }

                                isInsidePopUp = true;

                                popupBody.scrollTo({
                                    top: scrollToElement.getBoundingClientRect().top - popupBody.getBoundingClientRect().top + popupBody.scrollTop - 10,
                                    behavior: 'smooth',
                                });

                                break;
                            }

                            !isInsidePopUp && scrollToContentElement(scrollToElement);
                        }
                        else
                        {
                            scrollToTop();

                            for (const popupBody of [...document.querySelectorAll('.popup .popup-body')])
                            {
                                popupBody.scrollTo(0, 0);
                            }
                        }
                    }

                    if (!options.noMessage && response.data.message)
                    {
                        if (options.messageType === 'flashMessenger')
                        {
                            FlashMessenger.addErrorMessage(response.data.message);
                        }
                        else if (options.messageType === 'topMessage')
                        {
                            TopMessageHandler.error(response.data.message);
                        }
                    }

                    reject(response);
                });
            }
        }).catch((error) =>
        {
            if ('none' !== options.messageType)
            {
                errorHandler(error);
            }

            reject(error);
        })
        .finally(() =>
        {
            if (true !== options.noLoaderHide && true !== options.noLoader)
            {
                Loader.hide();
            }
        });
    });
}

function getFormData(form: HTMLFormElement, additionallyData?: [] | {}): Record<string, any>
{
    let l                     = form.elements.length,
        submittableRegex      = /^(input|select|textarea)$/i,
        submitInputTypesRegex = /^(submit|button)$/i,
        checkableInputTypes   = /^(checkbox|radio)$/i,
        data                  = {};

    function isValidFormElement(e: HTMLInputElement, includeSubmitType: boolean = false)
    {
        // return if form-element-name exists and is not disabled
        return 'object' === typeof e && e instanceof HTMLElement && e.name && !e.disabled
            && (
                // and is a submittable element and not a submit input-type
                submittableRegex.test(e.nodeName)
                && (includeSubmitType || !submitInputTypesRegex.test(e.type)
                )
                // and a not-checkable input-type or checked
                && (
                    !checkableInputTypes.test(e.type)
                    || e.checked
                )
            );
    }

    function getElementValue(e: HTMLSelectElement | HTMLInputElement)
    {
        let value;
        let nodeName = e.nodeName.toLowerCase();

        if ('select' === nodeName && e.multiple && e instanceof HTMLSelectElement)
        {
            value = [];
            if (e.options)
            {
                for (let k in e.options)
                {
                    if (e.options.hasOwnProperty(k) && e.options[k].selected)
                    {
                        value[value.length] = e.options[k].value;
                    }
                }
            }
            // set to null to prevent [''] @ php
            if (!value.length)
            {
                value = null;
            }
        }
        else if ('input' === nodeName && 'file' === e.type && e instanceof HTMLInputElement)
        {
            if (e.files?.length)
            {
                value = [];

                for (let k in e.files)
                {
                    if (e.files.hasOwnProperty(k) || !isNaN(+k))
                    {
                        value[value.length] = e.files[k];
                    }
                }
            }
        }
        else
        {
            // date input
            if (e.hasAttribute('data-selected-date'))
            {
                value = e.getAttribute('data-selected-date');
            }
            // other input fields
            else
            {
                value = e.value;
            }
        }

        return value;
    }

    for (let i = 0; i < l; i++)
    {
        let element: HTMLInputElement = form.elements[i] as HTMLInputElement;
        if (isValidFormElement(element))
        {
            let elementValue = getElementValue(element);
            if ('undefined' !== typeof elementValue)
            {
                if ('input' === element.nodeName.toLowerCase() && 'file' !== element.type.toLowerCase() && element.name.slice(-2) === '[]')
                {
                    if ('undefined' === typeof data[element.name])
                    {
                        data[element.name] = [];
                    }

                    data[element.name].push(elementValue);
                }
                else
                {
                    data[element.name] = elementValue;
                }
            }
        }
    }

    if (typeof additionallyData !== 'undefined')
    {
        for (let k in additionallyData)
        {
            if (additionallyData.hasOwnProperty(k))
            {
                let v = additionallyData[k];

                if (isValidFormElement(v, true))
                {
                    let elementValue = getElementValue(v);
                    if (typeof elementValue !== 'undefined')
                    {
                        data[v.name] = elementValue;
                    }
                }
                else if (Array.isArray(v))
                {
                    for (let i = 0; i < v.length; i++)
                    {
                        let it = v[i];
                        if ('object' === typeof it && it !== null)
                        {
                            data[`${k}[${i}]`] = JSON.stringify(it);
                        }
                        else
                        {
                            data[`${k}[${i}]`] = it;
                        }
                    }
                }
                else if ('string' === typeof v || 'object' === typeof v && 'undefined' !== typeof v?.length || 'number' === typeof v)
                {
                    data[k] = v;
                }
            }
        }
    }

    return data;
}

function normaliseValue(value: any): any
{
    if ('boolean' === typeof value)
    {
        return value ? 1 : 0;
    }

    return value;
}

function parseFormData(data: [] | {}): FormData
{
    let params = new FormData();
    for (let k in data)
    {
        if (data.hasOwnProperty(k) && null !== data[k])
        {
            if ('object' === typeof data[k] && 'undefined' !== data[k].length && data[k].length)
            {
                for (let i = 0; i < data[k].length; i++)
                {
                    params.append(k, normaliseValue(data[k][i]));
                }
            }
            else
            {
                params.append(k, normaliseValue(data[k]));
            }
        }
    }
    return params;
}

function parseQueryData(data: [] | {}): String
{
    let params = [];
    for (let k in data)
    {
        if (data.hasOwnProperty(k) && null !== data[k])
        {
            if ('object' === typeof data[k] && 'undefined' !== data[k].length && data[k].length)
            {
                for (let i = 0; i < data[k].length; i++)
                {
                    params.push(encodeURIComponent(k) + '=' + encodeURIComponent(normaliseValue(data[k][i])));
                }
            }
            else
            {
                params.push(encodeURIComponent(k) + '=' + encodeURIComponent(normaliseValue(data[k])));
            }
        }
    }
    return params.join('&');
}

const validators = {
    Between,
    DateTime,
    DateValidator,
    EmailAddress,
    NotEmailAddress,
    ExplodeValidator,
    GreaterThan,
    Hostname,
    Iban,
    Identical,
    InArray,
    LessThan,
    NotEmpty,
    NotInArray,
    NumberValidator,
    Regex,
    StringLength,
    Time,
    Vatin,
    BuyerReference,
    PhoneNumber,
};

const filters = {
    StripTags,
    StringTrim,
    ToInt,
    ToArray,
    ToString,
    ToNull,
    AddProtocol,
};

function executeFilter(filterName: string, value: any, options: FilterOptions): string
{
    if (typeof filters[filterName] === 'undefined')
    {
        if ('production' === process.env.NODE_ENV)
        {
            console.error('invalid filter ' + filterName);
        }
        else
        {
            throw new Error('invalid filter ' + filterName);
        }

        return value;
    }

    let validator = new filters[filterName](value, options);

    return validator.filter(value);
}

function executeValidator(validatorName: string, value: any, options: ValidatorOptions, context = {}): ValidatorResponse
{
    if (typeof validators[validatorName] === 'undefined')
    {
        if ('production' === process.env.NODE_ENV)
        {
            console.error('invalid validator ' + validatorName);
        }
        else
        {
            throw new Error('invalid validator ' + validatorName);
        }

        return {
            validate: true,
            error: '',
        };
    }

    let validator = new validators[validatorName](value, options, context);

    return {
        validate: validator.run() || validator.error.length === 0,
        error: validator.error,
    };
}

function resetRequired(value: number, key: string, vueComponent: ComponentPublicInstance, valueToValidate: any)
{
    if (value)
    {
        vueComponent.$store.dispatch('form/addValidator', {
            element: key,
            validator: {
                name: 'NotEmpty',
                break_chain_on_failure: true,
                toIndex: 0,
            },
        }).then(NoOperation);
    }
    else
    {
        vueComponent.$store.dispatch(
            'form/removeValidator',
            {
                element: key,
                name: 'NotEmpty',
            },
        ).then(NoOperation);
    }

    vueComponent.$store.dispatch('form/resetError', {errorName: key}).then(NoOperation);
    vueComponent.$store.dispatch(
        'form/validate',
        {
            value: valueToValidate,
            errorName: key,
            filterName: key,
        },
    ).then(NoOperation);
}

function getElementKeys(elementName: string, prefix: string = ''): { filter: string; name: string; }
{
    const filter = prefix ? `${prefix}.${elementName}` : elementName;
    const name   = prefix ? filter.split('.').map((v, k) => k ? `[${v}]` : v).join('') : elementName; // a[0][b]

    return {
        filter,
        name,
    };
}

export {
    filters,
    validators,
    executeValidator,
    executeFilter,
    getFormData,
    parseFormData,
    parseQueryData,
    formSubmit,
    resetRequired,
    getElementKeys,
};